import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Checkmark from 'components/common/icons/checkmark.jsx';
import ConversationItemType from 'models/conversation_item_type';
import i18n from 'scripts/i18n/i18n';
import { MenuItem as RawMenuItem } from 'components/lib/menu_deprecated';
import ViewOriginalEmailHtmlMenuItem from './view_original_email_html';

export default class ConversationItemMenuItems extends React.Component {
  render() {
    return (
      <React.Fragment>
        {this.renderReplyToGroupMessage()}
        {this.renderAddCommentMenuItem()}
        {this.renderForwardMenuItem()}
        {this.renderReplyMenuItem()}
        {this.renderNoReplyNeededItem()}
        {this.renderInstagramPublicReplyMenuItem()}
        {this.renderInstagramPrivateReplyMenuItem()}
        {this.renderViewOriginalEmailHtmlItem()}
        {this.renderVoiceRecordingMenuItems()}
        {this.renderCopyMenuItem()}
        {this.renderObserveCallMenuItem()}
        {this.renderViewSidekickConversation()}
        {this.renderMentionMenuItem()}
        {this.renderTranslateIntoMenuItem()}
        {this.renderToggleTranslationMenuItem()}
        {this.renderEditTaskMenuItem()}
        {this.renderFollowTaskMenuItem()}
        {this.renderUnfollowTaskMenuItem()}
        {this.renderReportPoorCallQuality()}
        {this.renderReactionMenuItem()}
        {this.renderUnreactionMenuItem()}
        {this.renderHighlightGroupedMessagesMenuItem()}
        {this.renderRedactMenuItem()}
        {this.renderHideFacebookCommentMenuItem()}
        {this.renderDeleteFacebookCommentMenuItem()}
      </React.Fragment>
    );
  }

  renderAddCommentMenuItem() {
    if (
      this.props.hasConversationGeneral &&
      !this.props.hideComment &&
      this.props.contentType === ConversationItemType.TASK &&
      !_.get(this.props, 'item.content.closedAt')
    ) {
      return (
        <MenuItem
          className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-addComment')}
          onSelect={this.props.openSidePanel}
        >
          <span>{i18n.t('Add a Comment')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderForwardMenuItem() {
    if (!this.props.isForwardVisible) {
      return null;
    }
    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-forward')}
        onSelect={this.props.onForward}
      >
        <span>{i18n.t('Forward')}</span>
      </MenuItem>
    );
  }

  renderReplyMenuItem() {
    if (!this.props.isReplyVisible) {
      return null;
    }
    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-reply')}
        onSelect={this.props.onReply}
      >
        <span>{i18n.t('Reply All')}</span>
      </MenuItem>
    );
  }

  renderInstagramPrivateReplyMenuItem() {
    if (!this.props.isInstagramPrivateReplyVisible) {
      return null;
    }

    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-instagramPrivateReply')}
        onSelect={this.props.onInstagramPrivateReply}
      >
        <span>{i18n.t('Reply with Instagram DM')}</span>
      </MenuItem>
    );
  }

  renderInstagramPublicReplyMenuItem() {
    if (!this.props.isInstagramPublicReplyVisible) {
      return null;
    }

    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-instagramPublicReply')}
        onSelect={this.props.onInstagramPublicReply}
      >
        <span>{i18n.t('Reply with Instagram comment')}</span>
      </MenuItem>
    );
  }

  renderNoReplyNeededItem() {
    if (!this.props.isNoReplyNeededVisible || !this.props.hasConversationGeneral) {
      return null;
    }
    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-noReplyNeeded')}
        onSelect={this.props.onNoReplyNeeded}
      >
        <span>{i18n.t('No Reply Needed')}</span>
      </MenuItem>
    );
  }

  renderViewOriginalEmailHtmlItem() {
    if (!this.props.isViewOriginalEmailHtmlVisible) {
      return null;
    }

    return <ViewOriginalEmailHtmlMenuItem />;
  }

  renderTranslateIntoMenuItem() {
    if (!this.props.isTranslateIntoVisible || !this.props.onTranslateInto || !this.props.isInbound) {
      return null;
    }
    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem')}
        data-aid="translate-into-menu-item"
        onSelect={() => this.props.onOpenTranslateMenu(this.props.onTranslateInto)}
      >
        <span>{i18n.t('Translate into')}</span>
      </MenuItem>
    );
  }

  renderToggleTranslationMenuItem() {
    if (!this.props.isToggleTranslationVisible || !this.props.onToggleTranslatedText) {
      return null;
    }
    const text = this.getToggleTranslationMenuItemText();

    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem')}
        data-aid="conversationItemMenu-menuItem-toggleTranslationMenuItem"
        onSelect={() => {
          this.props.onToggleTranslatedText();
        }}
      >
        <span>{text}</span>
      </MenuItem>
    );
  }

  getToggleTranslationMenuItemText() {
    if (this.props.isViewTranslation) {
      return i18n.t('See original message');
    }
    if (this.props.translatedLanguage) {
      return i18n.t('See message in {{language}}', { language: this.props.translatedLanguage });
    }
    return i18n.t('See translated message');
  }

  renderVoiceRecordingMenuItems() {
    if (!this.props.canManageVoiceRecordings) {
      return null;
    }
    return [
      <MenuItem
        className="conversationItemMenu-menuItem conversationItemMenu-menuItem-downloadRecording"
        key="conversationItemMenu-menuItem-downloadRecording"
      >
        <a className="conversationItemMenu-menuItem-downloadRecording-url" download href={this.props.recordingUrl}>
          <span>{i18n.t('Download Recording')}</span>
        </a>
      </MenuItem>,
      <MenuItem
        className="conversationItemMenu-menuItem conversationItemMenu-menuItem-deleteRecording"
        key="conversationItemMenu-menuItem-deleteRecording"
        onSelect={this.props.onDeleteRecording}
      >
        <span>{i18n.t('Delete Recording')}</span>
      </MenuItem>,
    ];
  }

  renderCopyMenuItem() {
    return (
      <MenuItem
        className="conversationItemMenu-menuItem conversationItemMenu-menuItem-copyLink"
        onSelect={this.props.onCopyLink}
      >
        <span>{i18n.t('Copy Link')}</span>
      </MenuItem>
    );
  }

  renderObserveCallMenuItem() {
    if (this.props.contentType === ConversationItemType.PHONE_CALL && this.props.canObserveCall) {
      return (
        <MenuItem
          className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-observeCall')}
          onSelect={this.props.onObserveCall}
        >
          <span>{i18n.t('Listen In On Call')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderViewSidekickConversation() {
    if (this.props.canViewSidekickConversation) {
      return (
        <MenuItem
          className={classnames(
            'conversationItemMenu-menuItem',
            'conversationItemMenu-menuItem-viewSidekickConversation'
          )}
          onSelect={this.props.onViewSidekickConversation}
        >
          <span>{i18n.t('View Gladly AI Conversation')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderMentionMenuItem() {
    if (!this.props.hasConversationGeneral) {
      return null;
    }
    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-mention')}
        onSelect={this.props.onOpenPinToCustomer}
      >
        <span>{i18n.t('Pin To Another Customer')}</span>
      </MenuItem>
    );
  }

  renderEditTaskMenuItem() {
    if (
      this.props.hasConversationGeneral &&
      this.props.contentType === ConversationItemType.TASK &&
      !_.get(this.props, 'item.content.closedAt')
    ) {
      return (
        <MenuItem
          className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-editTask')}
          onSelect={this.props.onEditTask}
        >
          <span>{i18n.t('Edit Task')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderFollowTaskMenuItem() {
    if (this.props.isFollowVisible && this.props.hasConversationGeneral) {
      return (
        <MenuItem
          className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-followTask')}
          onSelect={this.props.onFollowTask}
        >
          <span>{i18n.t('Follow Task')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderUnfollowTaskMenuItem() {
    if (this.props.isUnfollowVisible && this.props.hasConversationGeneral) {
      return (
        <MenuItem
          className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-unfollowTask')}
          onSelect={this.props.onUnfollowTask}
        >
          <span>{i18n.t('Unfollow Task')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderReportPoorCallQuality() {
    if (!this.props.canReportPoorCallQuality) {
      return null;
    }

    return (
      <MenuItem
        className="conversationItemMenu-menuItem conversationItemMenu-menuItem-reportPoorCallQuality"
        key="conversationItemMenu-menuItem-reportPoorCallQuality"
        onSelect={this.props.onReportPoorCallQuality}
      >
        <span>{i18n.t('Report Poor Call Quality')}</span>
      </MenuItem>
    );
  }

  renderReactionMenuItem() {
    if (this.props.isReactionVisible) {
      return (
        <MenuItem
          className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-reaction')}
          onSelect={this.props.onReaction}
        >
          <span>{i18n.t('Like Message')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderUnreactionMenuItem() {
    if (this.props.isUnreactionVisible) {
      return (
        <MenuItem
          className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-unreaction')}
          onSelect={this.props.onUnreaction}
        >
          <span>{i18n.t('Unlike Message')}</span>
        </MenuItem>
      );
    }
    return null;
  }

  renderRedactMenuItem() {
    if (!this.props.canRedactItem) {
      return null;
    }

    return (
      <RedactMenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-redact')}
        onSelect={this.props.onRedact}
      >
        <span>{i18n.t('Delete Message')}</span>
      </RedactMenuItem>
    );
  }

  renderHideFacebookCommentMenuItem() {
    if (!this.props.canHideFacebookComment) {
      return null;
    }

    return (
      <HideMenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-hideComment')}
        onSelect={this.props.onHideFacebookComment}
      >
        <span>{i18n.t('Hide comment')}</span>
      </HideMenuItem>
    );
  }

  renderDeleteFacebookCommentMenuItem() {
    if (!this.props.canDeleteFacebookComment) {
      return null;
    }

    return (
      <DeleteCommentMenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-deleteComment')}
        onSelect={this.props.onDeleteFacebookComment}
        showBorder={!this.props.canHideFacebookComment}
      >
        <span>{i18n.t('Delete comment')}</span>
      </DeleteCommentMenuItem>
    );
  }

  renderReplyToGroupMessage() {
    if (!this.props.isGroupMessagingItem || !this.props.canSendOutboundMessages) {
      return null;
    }

    return (
      <MenuItem
        className={classnames('conversationItemMenu-menuItem', 'conversationItemMenu-menuItem-replyToGroupMessage')}
        onSelect={this.props.onReplyToGroupMessage}
      >
        <span>{i18n.t('Reply to Message')}</span>
      </MenuItem>
    );
  }

  renderHighlightGroupedMessagesMenuItem() {
    if (!this.props.isGroupMessagingItem || !this.props.hasConversationGeneral) {
      return null;
    }

    return (
      <HighlightGroupedMessagesMenuItem
        className={classnames(
          'conversationItemMenu-menuItem',
          'conversationItemMenu-menuItem-highlightGroupedMessages'
        )}
        onSelect={
          this.props.isHighlighted
            ? this.props.onUnselectHighlightGroupedMessages
            : this.props.onHighlightGroupedMessages
        }
      >
        {this.props.isHighlighted && <ActiveCheckmark />}
        <span>{i18n.t('Highlight related messages')}</span>
      </HighlightGroupedMessagesMenuItem>
    );
  }
}

const ActiveCheckmark = styled(Checkmark)`
  fill: ${p => p.theme.colors.gray600};
  height: ${p => p.theme.spacing.large};
  left: 16px;
  position: absolute;
  top: 9px;
  width: ${p => p.theme.spacing.large};
`;

const MenuItem = styled(RawMenuItem)`
  padding: 8px 36px;
  position: relative;

  &:focus {
    padding: 8px 36px;

    svg {
      fill: ${p => p.theme.colors.green400};
    }
  }
`;

const HighlightGroupedMessagesMenuItem = styled(MenuItem)`
  border-top: 1px solid ${p => p.theme.colors.gray300} !important;
`;

const HideMenuItem = styled(MenuItem)`
  .menu &,
  .menu-container & {
    border-top: 1px solid ${p => p.theme.colors.gray300};
    &:focus {
      border-top: 1px solid ${p => p.theme.colors.gray300};
    }
  }
`;

const DeleteCommentMenuItem = styled(MenuItem)`
  color: ${p => p.theme.colors.red400};

  &:hover {
    background: ${p => p.theme.colors.red400};
    color: ${p => p.theme.colors.white};
  }

  .menu &,
  .menu-container & {
    ${p => p.showBorder && `border-top: 1px solid ${p.theme.colors.gray300};`}
  }
`;

const RedactMenuItem = styled(MenuItem)`
  border-top: 1px solid ${p => p.theme.colors.gray300};
  color: ${p => p.theme.colors.red400};
  &:hover {
    background: ${p => p.theme.colors.red400};
    color: ${p => p.theme.colors.white};
  }
`;

ConversationItemMenuItems.propTypes = {
  canDeleteFacebookComment: PropTypes.bool,
  canHideFacebookComment: PropTypes.bool,
  canManageVoiceRecordings: PropTypes.bool,
  canObserveCall: PropTypes.bool,
  canRedactItem: PropTypes.bool,
  canReportPoorCallQuality: PropTypes.bool,
  canSendOutboundMessages: PropTypes.bool,
  canViewSidekickConversation: PropTypes.bool,
  className: PropTypes.string,
  contentType: PropTypes.string.isRequired,
  conversationItemId: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  hideComment: PropTypes.bool,
  isFollowVisible: PropTypes.bool,
  isForwardVisible: PropTypes.bool,
  isGroupMessagingItem: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  isInbound: PropTypes.bool,
  isInstagramPrivateReplyVisible: PropTypes.bool,
  isInstagramPublicReplyVisible: PropTypes.bool,
  hasConversationGeneral: PropTypes.bool,
  isMenuVisible: PropTypes.bool,
  isNoReplyNeededVisible: PropTypes.bool,
  isReactionVisible: PropTypes.bool,
  isReplyVisible: PropTypes.bool,
  isToggleTranslationVisible: PropTypes.bool,
  isTranslateIntoVisible: PropTypes.bool,
  isUnfollowVisible: PropTypes.bool,
  isUnreactionVisible: PropTypes.bool,
  isViewOriginalEmailHtmlVisible: PropTypes.bool,
  isViewTranslation: PropTypes.bool,
  item: PropTypes.object,
  onCopyLink: PropTypes.func,
  onDeleteRecording: PropTypes.func,
  onEditTask: PropTypes.func,
  onFollowTask: PropTypes.func,
  onForward: PropTypes.func,
  onHighlightGroupedMessages: PropTypes.func,
  onInstagramPrivateReply: PropTypes.func,
  onInstagramPublicReply: PropTypes.func,
  onNoReplyNeeded: PropTypes.func,
  onObserveCall: PropTypes.func,
  onHideFacebookComment: PropTypes.func,
  onDeleteFacebookComment: PropTypes.func,
  onOpenPinToCustomer: PropTypes.func,
  onOpenTranslateMenu: PropTypes.func,
  onReaction: PropTypes.func,
  onRedact: PropTypes.func,
  onReply: PropTypes.func,
  onReplyToGroupMessage: PropTypes.func,
  onReportPoorCallQuality: PropTypes.func,
  onToggleTranslatedText: PropTypes.func,
  onTranslateInto: PropTypes.func,
  onUnfollowTask: PropTypes.func,
  onUnreaction: PropTypes.func,
  onUnselectHighlightGroupedMessages: PropTypes.func,
  onViewSidekickConversation: PropTypes.func,
  openSidePanel: PropTypes.func,
  recordingUrl: PropTypes.string,
  translatedLanguage: PropTypes.string,
};
